import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import ZoobbeSelect from '../Global/ZoobbeSelect';

import './scss/AddChecklist.scss';
import { fetchChecklists, localAddChecklist, localNewChecklist, revertChecklistAddition } from '../../redux/Slices/checklistSlice';
import { togglePopover } from '../../redux/Slices/popoverSlice';
import { config } from '../../config';
import { fetchActivities } from '../../redux/Slices/activitiesSlice';
import SearchSelect from '../Global/SearchSelect';
import Checkbox from '../Global/Checkbox';
import Spinner from '../Global/Spinner';

const AddChecklist = () => {
    const [title, setTitle] = useState('Checklist');
    const [selectedChecklist, setSelectedChecklist] = useState(null);
    const [loading, setLoading] = useState(false);
    const [autoGenerate, setAutoGenerate] = useState(false);
    const [generatingItems, setGeneratingItems] = useState(false);
    const [generatedItems, setGeneratedItems] = useState([]);

    const inputRef = useRef(null);
    const { card } = useSelector((state) => state.card);
    const cardId = card.shortId;


    const dispatch = useDispatch();

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, []);

    const checklistOptions = [
        { value: 'none', label: '(none)' },
    ];

    const handleChecklistSelect = (checklist) => {
        setSelectedChecklist(checklist);
    };

    // Function to generate checklist items using AI
    const generateChecklistItems = async () => {
        if (!card?.title) return;

        setGeneratingItems(true);
        try {
            const response = await fetch(`${config.API_URI}/v1/ai/checklist-items`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    title: card.title,
                    description: card.description || '',
                    boardTitle: card.board?.title || 'Project Board'
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.items) {
                    setGeneratedItems(data.items);
                } else {
                    console.warn('Failed to generate items:', data.error);
                    setGeneratedItems([]);
                }
            } else {
                console.error('API request failed:', response.status);
                setGeneratedItems([]);
            }
        } catch (error) {
            console.error('Failed to generate checklist items:', error);
        } finally {
            setGeneratingItems(false);
        }
    };

    // Generate items when auto-generate is enabled
    useEffect(() => {
        if (autoGenerate && card?.title) {
            generateChecklistItems();
        } else {
            setGeneratedItems([]);
        }
    }, [autoGenerate, card?.title]);

    const handleAddChecklist = async () => {
        if (!title) {
            alert("Please enter a title for the checklist");
            return;
        }

        setLoading(true);

        try {
            
            const response = await fetch(config.API_URI + `/v1/cards/${cardId}/checklists`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    
                },
                credentials: 'include',
                body: JSON.stringify({ title })
            });

            if (response.ok) {
                const data = await response.json();
                const checklist = data.checklist;

                dispatch(localAddChecklist(checklist));

                // If auto-generate is enabled and we have generated items, add them
                if (autoGenerate && generatedItems.length > 0) {
                    await addGeneratedItems(checklist._id);
                }

                // Close the popover and reset the form
                dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
                setTitle('Checklist');
                setAutoGenerate(false);
                setGeneratedItems([]);

                // Fetch the updated activities
                dispatch(fetchActivities({ cardId }));
            } else {
                console.error('Failed to add checklist');
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Function to add generated items to the checklist
    const addGeneratedItems = async (checklistId) => {
        try {
            

            // Add each generated item to the checklist
            for (const itemTitle of generatedItems) {
                await fetch(config.API_URI + `/v1/cards/${cardId}/checklists/${checklistId}/items`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        
                    },
                    credentials: 'include',
                    body: JSON.stringify({ title: itemTitle, checked: false })
                });
            }

            // Refresh checklists to show the new items
            dispatch(fetchChecklists(cardId));
        } catch (error) {
            console.error('Failed to add generated items:', error);
        }
    };


    return (
        <div className="add-checklist">
            <h2>Add checklist</h2>

            <div className="group">
                <h3>Title</h3>
                <input
                    type="text"
                    placeholder="Checklist"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    ref={inputRef}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault(); // Prevents form submission if inside a form
                            handleAddChecklist();
                        }
                    }}
                />

            </div>
            <div className="group">
                <h3>Copy items from…</h3>
                <SearchSelect
                    options={checklistOptions}
                    onSelect={handleChecklistSelect}
                    placeholder="Select an option"
                    isGrouped={false}
                    defaultValue={'none'}
                />
            </div>

            <div className="group">
                <div className="auto-generate-option">
                    <Checkbox
                        checked={autoGenerate}
                        onChange={setAutoGenerate}
                        id="auto-generate-checklist"
                    />
                    <label htmlFor="auto-generate-checklist">
                        <h3>Auto-generate items using AI</h3>
                        <p>Generate checklist items based on the card title and description</p>
                    </label>
                </div>

                {autoGenerate && (
                    <div className="generated-items-preview">
                        {generatingItems ? (
                            <div className="generating-items">
                                <Spinner size={16} />
                                <span>Generating items...</span>
                            </div>
                        ) : generatedItems.length > 0 ? (
                            <div className="items-preview">
                                <h4>Generated items ({generatedItems.length}):</h4>
                                <ul>
                                    {generatedItems.slice(0, 3).map((item, index) => (
                                        <li key={index}>{item}</li>
                                    ))}
                                    {generatedItems.length > 3 && (
                                        <li className="more-items">...and {generatedItems.length - 3} more</li>
                                    )}
                                </ul>
                            </div>
                        ) : card?.title ? (
                            <div className="no-items-generated">
                                <p>No items could be generated. Try adding a more descriptive title or description.</p>
                            </div>
                        ) : (
                            <div className="no-card-title">
                                <p>Add a title to the card to generate checklist items.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <button onClick={handleAddChecklist} disabled={loading || generatingItems}>
                {loading ? 'Creating...' : generatingItems ? 'Generating...' : 'Create'}
            </button>
        </div>
    );
};

export default AddChecklist;
